import { navigateTo } from "@util/routerService";
import { clearLocalStorage, readLocalStorage } from "@util/safeStorage";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
// const fs = require('fs');
// import * as fs from 'fs';

axios.defaults.headers.common.Accept = "application/json";
axios.defaults.timeout = 12000;
axios.defaults.withCredentials = false;

export { readLocalStorage, writeLocalStorage } from "@util/safeStorage";

const PUBLIC_SHARED_LINK_SEGMENTS = new Set([
  "feedback",
  "invoice",
  "membershipinvoice",
  "packageinvoice",
  "giftcardinvoice",
  "advanceinvoice",
  "advancerefund",
  "balanceinvoice",
  "payment",
  "orderconfirmation",
  "form",
]);

/**
 * Shared links are always /<tenant>/<store>/<page>/<id>, so only the third segment counts. Matching
 * any segment would let a store slug such as "payment" disable auth for that whole store.
 */
const isPublicSharedLinkRoute = () => {
  if (typeof window === "undefined") return false;
  const segments = window.location.pathname.split("/").filter(Boolean);
  const page = segments[2];
  return Boolean(page) && PUBLIC_SHARED_LINK_SEGMENTS.has(page.toLowerCase());
};

const apiProxyMap: Record<string, string> = (() => {
  try {
    return JSON.parse(process.env.NEXT_PUBLIC_API_PROXY_MAP || "{}");
  } catch (error) {
    return {};
  }
})();

/**
 * Rewrites an absolute backend URL onto this app's own origin so the browser only ever talks
 * to port 443. See the API proxy notes in next.config.js. Server side requests keep going
 * direct, since the Next.js server can reach the backend ports.
 */
const toSameOriginUrl = (url: string): string => {
  if (typeof window === "undefined" || !url) return url;
  const origin = Object.keys(apiProxyMap).find(
    (o) => url === o || url.startsWith(`${o}/`),
  );
  return origin ? `${apiProxyMap[origin]}${url.slice(origin.length)}` : url;
};

const getUserDataFromCookie = (key: string) => {
  if (typeof document === "undefined") return null;

  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${key}=`);

    if (parts.length === 2) {
      const cookieString = parts.pop()?.split(";").shift();
      if (cookieString) {
        return JSON.parse(decodeURIComponent(cookieString));
      }
    }
  } catch (error) {
    console.error("Failed to parse user_data cookie for headers", error);
  }
  return null;
};

const getHttpHeaders = (headers: any = {}): AxiosRequestConfig => {
  const customHeaders: any = { ...headers, isCatalogue: true };

  let userData = getUserDataFromCookie("user_data_cat");
  if (!userData) {
    userData = getUserDataFromCookie("user");
  }

  if (!userData) {
    const localData = readLocalStorage("User_Data");
    if (localData) {
      const dataParts = localData.split("_");
      userData = {
        tenantId: dataParts[1] || null,
        storeId: dataParts[2] || null,
      };
    }
  }

  if (userData) {
    if (userData.tenantId)
      customHeaders["tenantid"] = userData.tenantId.toString();
    if (userData.storeId)
      customHeaders["storeid"] = userData.storeId.toString();
  }

  customHeaders["check"] = "OK";
  const authToken = isPublicSharedLinkRoute()
    ? null
    : readLocalStorage("Auth_Token_Value");
  if (authToken) {
    customHeaders["Authorization"] = authToken;
  }

  const config: AxiosRequestConfig = {
    headers: customHeaders,
  };

  if (typeof window === "undefined") {
    const https = require("https");
    config.httpsAgent = new https.Agent({ rejectUnauthorized: false });
  }

  return config;
};

const logout = async () => {
  console.warn("401/403 Unauthorized detected. Logging out the user...");

  if (typeof window !== "undefined") {
    try {
      await POST(`${process.env.NEXT_PUBLIC_BASE_PCS_URL}/logout`, {});
    } catch (error) {
      console.error(
        "Backend logout failed, proceeding with local cleanup.",
        error,
      );
    } finally {
      document.cookie = "user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "lvt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

      clearLocalStorage();
      navigateTo("home");
    }
  }
};

// --- Axios Interceptor ---
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      if (
        !isPublicSharedLinkRoute() &&
        error.config &&
        error.config.url &&
        !error.config.url.includes("logout") &&
        !error.config.url.includes("guestReferralInvite") &&
        !error.config.url.includes("referralConfig")
      ) {
        logout();
      }
    }
    return Promise.reject(error);
  },
);

/**
 * The catalogue backend throttles at 25 requests / 10s per client IP, and mobile carriers put many
 * customers behind one NAT address. Re-renders that refire the same GET burn that shared budget and
 * surface as a 429, so identical in-flight GETs share a single response.
 */
const inFlightGets = new Map<string, Promise<AxiosResponse>>();

const GET = (path: string, headers: any = {}): Promise<AxiosResponse> => {
  const url = toSameOriginUrl(path);
  const config = getHttpHeaders(headers);

  if (typeof window === "undefined") return axios.get(url, config);

  const key = `${url}|${config.headers?.tenantid ?? ""}|${config.headers?.storeid ?? ""}`;
  const pending = inFlightGets.get(key);
  if (pending) return pending;

  const request = axios
    .get(url, config)
    .finally(() => inFlightGets.delete(key));
  inFlightGets.set(key, request);
  return request;
};

const DELETE = (path: string, headers: any = {}): Promise<AxiosResponse> =>
  axios.delete(toSameOriginUrl(path), getHttpHeaders(headers));

const POST = (
  path: string,
  data: any,
  headers: any = {},
): Promise<AxiosResponse> =>
  axios.post(toSameOriginUrl(path), data, getHttpHeaders(headers));

const PUT = (
  path: string,
  data: any,
  headers: any = {},
): Promise<AxiosResponse> =>
  axios.put(toSameOriginUrl(path), data, getHttpHeaders(headers));

const PATCH = (
  path: string,
  data: any,
  headers: any = {},
): Promise<AxiosResponse> =>
  axios.post(toSameOriginUrl(path), data, getHttpHeaders(headers));

export const APISERVICE = { GET, DELETE, POST, PUT, PATCH };
